import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSnapshot } from 'valtio';
import { useNavigate, useLocation } from 'react-router';
import { HistoryStore, HistoryItem } from '@/utils/store/store';
import { supabaseAuthStore } from '@/utils/store/supabaseAuth';
import CommonLoading from '@/components/CommonLoading';
import SidebarFoot from './SidebarFoot';
import more from "@/pages/AfterMeet/static/more.png";
import {Empty} from "antd";
import SBApp from './SBApp';
import { ResultModal } from '@/pages/AfterMeet/ResultModal';

/**
 * 左侧边栏组件，包含导航和历史记录
 */
const Sidebar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // 只使用 Supabase 认证状态
    const supabaseAuth = useSnapshot(supabaseAuthStore);
    const { data: historyData, runFetch, isLoading } = useSnapshot(HistoryStore);

    useEffect(() => {
        if (supabaseAuth.isAuthenticated && supabaseAuth.id) {
            runFetch(supabaseAuth.id);
        }
    }, [supabaseAuth.isAuthenticated, supabaseAuth.id, runFetch]);



    const historyLoading = supabaseAuth.isLoading || isLoading;

    const isEmpty = historyData
        &&  historyData[0].length === 0
        &&  historyData[1].length === 0
        &&  historyData[2].length === 0;

    useEffect(() => {

    }, [historyLoading,isEmpty]);

  return (
    <aside className="responsive-sidebar bg-[#FBFBFA] border-r border-[#E9E9E7] h-full flex flex-col justify-between">
      <div className="flex flex-col flex-1 min-h-0">
        {/* 品牌标识 */}
        <div
          className="flex items-center gap-2 px-5 py-4 mt-3 mb-4 cursor-pointer group"
          onClick={() => navigate('/')}
        >
          <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white font-bold text-xl transition-transform group-hover:scale-105">
            N
          </div>
          <span className="text-lg font-semibold tracking-tight text-[#37352F]">NivoWork</span>
        </div>

        {/* 功能导航 */}
        <nav className="px-3 space-y-1">
          <button
            onClick={() => navigate('/inMeet')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer ${
              location.pathname === '/inMeet'
                ? 'bg-[#EFEFED] text-[#37352F]'
                : 'text-[#37352F] hover:bg-[#EFEFED]'
            }`}
          >
            {/* 麦克风图标 */}
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
            实时会议
          </button>

          <button
            onClick={() => navigate('/afterMeet')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer ${
              location.pathname === '/afterMeet'
                ? 'bg-[#EFEFED] text-[#37352F]'
                : 'text-[#37352F] hover:bg-[#EFEFED]'
            }`}
          >
            {/* 文档整理图标 */}
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            会后整理
          </button>
        </nav>

        {/* 历史记录区域 */}
        <div className="mt-8 px-3 flex-1 min-h-0 overflow-y-auto" style={{scrollbarWidth:"none"}}>
          <span className="text-[11px] font-bold text-[#91918E] uppercase tracking-wider mb-2 block px-2">
            历史记录
          </span>

          {historyLoading && (
            <div className="py-4">
              <CommonLoading />
            </div>
          )}

          {!historyLoading && isEmpty && (
            <p className="text-xs text-[#91918E] py-2 px-2">暂无记录</p>
          )}

          {!historyLoading && !isEmpty && (
            <HistoryListView historyData={historyData} userId={supabaseAuth.id || ''} />
          )}
        </div>
      </div>

      {/* 底部操作区域 */}
      <SidebarFoot />
    </aside>
  );
};

/**
 * 历史记录列表视图 - 默认近7天（合并今天+前7天），可展开近30天
 */
const HistoryListView: React.FC<{ historyData: any; userId: string }> = ({ historyData, userId }) => {
  const [showAll, setShowAll] = useState(false);

  // 近7天 = 今天 + 前7天
  const recentList = [...(historyData?.[0] || []), ...(historyData?.[1] || [])];
  // 近30天额外部分
  const olderList = historyData?.[2] || [];
  const hasOlder = olderList.length > 0;

  const displayList = showAll ? [...recentList, ...olderList] : recentList;

  return (
    <div className="text-[12px]">
      <div className="space-y-0.5">
        {displayList.map((item: HistoryItem) => (
          <HistoryItemRow key={item.id} item={item} userId={userId} />
        ))}
      </div>
      {hasOlder && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full text-center text-[11px] text-[#91918E] hover:text-[#73726E] py-3 cursor-pointer transition-colors"
        >
          {showAll ? '收起，仅显示近7天' : `展开近30天 (${olderList.length}条更早记录)`}
        </button>
      )}
    </div>
  );
};

/**
 * 单条历史记录行 - Notion 风格，带悬浮菜单
 */
const HistoryItemRow: React.FC<{ item: HistoryItem; userId: string }> = ({ item, userId }) => {
  const { delItem, genTitle, titleLoadingMap } = useSnapshot(HistoryStore);

  const getDisplayTitle = () => {
    if (titleLoadingMap[item.id]) {
      return <CommonLoading />;
    }
    return item.title || (() => { try { return JSON.parse(item.result)["default"]; } catch { return '未命名记录'; } })();
  };

  return (
    <div className="group flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-[#EFEFED] cursor-pointer transition-colors duration-150">
      {/* 文档小图标 */}
      <svg className="w-4 h-4 flex-shrink-0 text-[#91918E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
      <span
        className="flex-1 truncate text-[13px] text-[#73726E]"
        onClick={() => ResultModal(item)}
      >
        {getDisplayTitle()}
      </span>
      {/* 悬浮更多菜单 */}
      <div className="group/menu relative">
        <button className="w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-[#E9E9E7] transition-all cursor-pointer">
          <svg className="w-4 h-4 text-[#91918E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
          </svg>
        </button>
        <ul className="menu bg-white rounded-lg fixed z-[9999] w-32 p-1 shadow-lg border border-[#E9E9E7] opacity-0 group-hover/menu:opacity-100 transition-opacity duration-200 pointer-events-none group-hover/menu:pointer-events-auto">
          <li className="text-[12px] text-[#37352F]">
            <a onClick={() => delItem(userId, item.id)} className="flex items-center gap-2 px-3 py-1.5 rounded hover:bg-[#F7F7F5]">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              删除
            </a>
          </li>
          <li className="text-[12px] text-[#37352F]">
            <a onClick={() => genTitle(userId, item.id)} className="flex items-center gap-2 px-3 py-1.5 rounded hover:bg-[#F7F7F5]">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              </svg>
              推理标题
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
